import React, { useEffect, useState } from "react";
import { Calendar, Search } from "lucide-react";
import { toast } from "sonner";

import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

/**
 * Modal for admin to book an appointment on behalf of a customer.
 * Opens from the "Book appointment" button on /admin/customers/:id.
 *
 * Hits POST /api/admin/customers/:customer_id/appointments — creates a
 * source="admin_created" lead + a scheduled appointment. Returns the new
 * appointment + lead_id.
 */
export const AdminBookAppointmentButton = ({ customerId, customerName, onBooked }) => {
  const [open, setOpen] = useState(false);
  const [vehicleQuery, setVehicleQuery] = useState("");
  const [vehicles, setVehicles] = useState([]);
  const [selected, setSelected] = useState(null);
  const [dt, setDt] = useState("");
  const [duration, setDuration] = useState(30);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/vehicles", { params: { q: vehicleQuery || undefined, page_size: 12 } });
        setVehicles(data.items || []);
      } catch { setVehicles([]); }
      finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [vehicleQuery, open]);

  const submit = async () => {
    if (!selected || !dt) {
      toast.error("Pick a vehicle and a time.");
      return;
    }
    setSaving(true);
    try {
      const isoDt = new Date(dt).toISOString();
      const { data } = await api.post(`/admin/customers/${customerId}/appointments`, {
        vehicle_id: selected.id,
        scheduled_at: isoDt,
        duration_minutes: duration,
        notes: notes.trim() || null,
      });
      toast.success(`Appointment booked for ${customerName}`);
      setOpen(false);
      setSelected(null); setDt(""); setNotes(""); setVehicleQuery("");
      onBooked?.(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  // Default: 2 days from now, 10:00 AM local
  useEffect(() => {
    if (open && !dt) {
      const d = new Date(); d.setDate(d.getDate() + 2); d.setHours(10, 0, 0, 0);
      // datetime-local wants YYYY-MM-DDTHH:mm (no timezone)
      const pad = (n) => String(n).padStart(2, "0");
      setDt(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`);
    }
  }, [open, dt]);

  return (
    <>
      <Button onClick={() => setOpen(true)} data-testid="admin-book-appt-btn">
        <Calendar size={14} /> Book appointment
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="admin-book-appt-dialog">
          <DialogHeader>
            <DialogTitle>Book appointment for {customerName}</DialogTitle>
            <DialogDescription>Creates a lead (source: admin_created) + a scheduled appointment. The BDC team will see it in their queue.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Vehicle</Label>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input className="pl-9" placeholder="Search inventory…" value={vehicleQuery} onChange={(e) => { setVehicleQuery(e.target.value); setSelected(null); }} data-testid="admin-book-veh-search" />
              </div>
              <div className="mt-1 rounded-md border border-slate-200 max-h-40 overflow-y-auto">
                {loading ? <p className="text-xs text-slate-400 p-3 text-center">Searching…</p>
                  : vehicles.length === 0 ? <p className="text-xs text-slate-400 p-3 text-center">No matches.</p>
                  : vehicles.map((v) => (
                    <button
                      key={v.id}
                      type="button"
                      onClick={() => setSelected(v)}
                      className={`w-full text-left px-3 py-2 border-b border-slate-100 last:border-0 hover:bg-slate-50 ${selected?.id === v.id ? "bg-brand/5" : ""}`}
                      data-testid={`admin-book-veh-${v.id}`}
                    >
                      <div className="text-sm font-medium">{v.year} {v.make} {v.model} {v.trim || ""}</div>
                      <div className="text-xs text-slate-500">${v.price?.toLocaleString?.() || "—"} · {v.dealer_name}</div>
                    </button>
                  ))
                }
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Scheduled at</Label>
                <Input type="datetime-local" value={dt} onChange={(e) => setDt(e.target.value)} data-testid="admin-book-datetime" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Duration (min)</Label>
                <Input type="number" min={15} max={240} step={15} value={duration} onChange={(e) => setDuration(parseInt(e.target.value) || 30)} data-testid="admin-book-duration" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Internal notes</Label>
              <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional context for the BDC team" data-testid="admin-book-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={saving || !selected || !dt} data-testid="admin-book-submit">
              {saving ? "Booking…" : "Book appointment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AdminBookAppointmentButton;
